import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import apiClient from '../api/client';
import './RecalcularValores.css';

/**
 * Página para Admin recalcular valores financeiros da rodada
 */

const RecalcularValores = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [rodada, setRodada] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processando, setProcessando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    if (id) {
      carregarRodada();
    }
  }, [isAdmin, navigate, id]);

  const carregarRodada = async () => {
    try {
      setLoading(true);
      const rodadaRes = await apiClient.get(`/rodadas/${id}`);
      setRodada(rodadaRes.data);
    } catch (err) {
      console.error('Erro ao carregar rodada:', err);
      setError(err.response?.data?.error || 'Erro ao carregar rodada');
    } finally {
      setLoading(false);
    }
  };

  const handleRecalcular = async () => {
    if (!window.confirm(
      'Deseja recalcular os valores financeiros desta rodada?\n\n' +
      'Isso verificará os valores atuais da configuração e mostrará como serão calculados ao fechar a rodada.\n\n' +
      'Os valores serão aplicados automaticamente ao fechar a rodada.'
    )) {
      return;
    }

    try {
      setProcessando(true);
      setError('');
      setResultado(null);

      const response = await apiClient.post(`/rodadas/${id}/recalcular-valores`);
      setResultado(response.data);
    } catch (err) {
      console.error('Erro ao recalcular valores:', err);
      setError(err.response?.data?.error || 'Erro ao recalcular valores');
    } finally {
      setProcessando(false);
    }
  };

  if (loading) {
    return <div className="container">Carregando...</div>;
  }

  if (!rodada) {
    return (
      <div className="container">
        <div className="error">Rodada não encontrada.</div>
        <button onClick={() => navigate('/admin/menu')} className="btn btn-primary">
          Voltar ao Menu
        </button>
      </div>
    );
  }

  if (rodada.status !== 'aberta') {
    return (
      <div className="container">
        <div className="error">Esta funcionalidade só está disponível para rodadas abertas.</div>
        <button onClick={() => navigate('/admin/menu')} className="btn btn-primary">
          Voltar ao Menu
        </button>
      </div>
    );
  }

  return (
    <div className="container recalcular-valores-container">
      <div className="page-header">
        <h1>🔄 Recalcular Valores Financeiros</h1>
        <button onClick={() => navigate('/admin/menu')} className="btn btn-secondary">
          ← Voltar
        </button>
      </div>

      <div className="rodada-info">
        <p><strong>Rodada:</strong> {new Date(rodada.data).toLocaleDateString('pt-BR')}</p>
        <p><strong>Status:</strong> {rodada.status}</p>
      </div>

      <div className="info-box">
        <p>
          <strong>ℹ️ Informação:</strong> Esta ferramenta verifica os valores atuais da configuração 
          e mostra como serão calculados os valores financeiros ao fechar a rodada.
        </p>
        <p style={{ marginTop: '10px' }}>
          <strong>Importante:</strong> Os valores serão aplicados automaticamente ao fechar a rodada. 
          Esta ferramenta apenas mostra uma prévia dos cálculos.
        </p>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="action-section">
        <button 
          onClick={handleRecalcular}
          disabled={processando}
          className="btn btn-primary btn-large"
        >
          {processando ? 'Processando...' : 'Recalcular Valores'}
        </button>
      </div>

      {resultado && (
        <div className="resultado-section">
          <h2>Resultado do Recalculo</h2>
          
          <div className="configuracao-box">
            <h3>Configuração Atual</h3>
            <ul>
              <li><strong>Rebuy Duplo:</strong> R$ {resultado.configuracao.rebuy_duplo_valor.toFixed(2)}</li>
              <li><strong>Rebuy Simples:</strong> R$ {resultado.configuracao.rebuy_simples_valor.toFixed(2)}</li>
              <li><strong>Entrada:</strong> R$ {resultado.configuracao.entrada_valor.toFixed(2)}</li>
              <li><strong>Add-on:</strong> R$ {resultado.configuracao.addon_valor.toFixed(2)}</li>
            </ul>
          </div>

          {resultado.resultados && resultado.resultados.atualizadas && resultado.resultados.atualizadas.length > 0 && (
            <div className="jogadores-box">
              <h3>Jogadores com Rebuy Duplo</h3>
              <table className="resultado-table">
                <thead>
                  <tr>
                    <th>Jogador</th>
                    <th>Qtd Rebuy Duplo</th>
                    <th>Valor Unitário</th>
                    <th>Valor Total</th>
                  </tr>
                </thead>
                <tbody>
                  {resultado.resultados.atualizadas
                    .filter(j => j.rebuy_duplo_qtd > 0)
                    .map((jogador, index) => {
                      return (
                        <tr key={index}>
                          <td>{jogador.jogador_nome}</td>
                          <td>{jogador.rebuy_duplo_qtd}</td>
                          <td>R$ {jogador.rebuy_duplo_valor_unitario?.toFixed(2) || resultado.configuracao.rebuy_duplo_valor.toFixed(2)}</td>
                          <td>R$ {jogador.rebuy_duplo_valor_novo.toFixed(2)}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}

          <div className="success-box">
            <p>
              <strong>✅ Valores atualizados!</strong> Os novos valores serão aplicados automaticamente 
              ao fechar a rodada.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecalcularValores;
